import {attachSearchBox, type SearchResult} from '../../../js/modules/search.ts';

const {appSubUrl} = window.config;

export function initAgentActionMatrix(): void {
  const rows = document.querySelectorAll<HTMLElement>('.js-agent-action-row');
  if (!rows.length) return;

  for (const row of rows) {
    const enabledInput = row.querySelector<HTMLInputElement>('.js-agent-capability');
    if (!enabledInput) continue;

    const settingsInputs = row.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('.js-agent-action-settings input, .js-agent-action-settings select, .js-agent-action-settings textarea');
    const settingsDropdowns = row.querySelectorAll<HTMLElement>('.js-agent-action-settings .ui.dropdown');
    const eventDropdowns = row.querySelectorAll<HTMLElement>('.js-agent-action-events-dropdown');
    const eventInputs = row.querySelectorAll<HTMLInputElement>('.js-agent-action-events-cell input');

    const setEnabledState = () => {
      const isEnabled = enabledInput.checked;
      row.classList.toggle('is-disabled', !isEnabled);
      for (const input of settingsInputs) {
        if (input === enabledInput) continue;
        if (input.name === enabledInput.name) continue;
        input.disabled = !isEnabled;
      }
      for (const input of eventInputs) {
        input.disabled = !isEnabled;
      }
      for (const dropdown of settingsDropdowns) {
        dropdown.classList.toggle('disabled', !isEnabled);
      }
      for (const dropdown of eventDropdowns) {
        dropdown.classList.toggle('disabled', !isEnabled);
      }
    };

    enabledInput.addEventListener('change', setEnabledState);
    setEnabledState();
  }

  initAgentSecurityScheduleToggle();
  initAgentActionUserPickers();
}

// the reviewer and assignee settings of an action accept several agents and several human users at
// the same time, the human users are kept as one hidden input per selected account
function initAgentActionUserPickers(): void {
  const pickers = document.querySelectorAll<HTMLElement>('.js-agent-user-picker');
  for (const picker of pickers) {
    const inputName = picker.getAttribute('data-input-name');
    const searchBox = picker.querySelector<HTMLElement>('.ui.search');
    const addButton = picker.querySelector<HTMLButtonElement>('.js-agent-user-picker-add');
    const list = picker.querySelector<HTMLElement>('.js-agent-user-picker-list');
    if (!inputName || !searchBox || !addButton || !list) continue;
    const removeText = list.getAttribute('data-remove-text') || 'Remove';

    attachSearchBox(searchBox, `${appSubUrl}/user/search_candidates?q={query}&orgs=false&bots=false`, (response: AgentPermissionUserSearchResponse) => {
      return response.data.map((item) => ({title: item.login, image: item.avatar_url, description: item.full_name}));
    });

    addButton.addEventListener('click', () => {
      const prompt = searchBox.querySelector<HTMLInputElement>('input.prompt');
      if (!prompt) return;
      const value = prompt.value.trim();
      prompt.value = '';
      if (!value) return;
      if (list.querySelector(`input[name="${CSS.escape(inputName)}"][value="${CSS.escape(value)}"]`)) return;

      const item = document.createElement('div');
      item.className = 'item tw-items-center js-agent-user-picker-item';
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      input.value = value;
      const label = document.createElement('div');
      label.className = 'item-main';
      label.textContent = value;
      const trailing = document.createElement('div');
      trailing.className = 'item-trailing';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ui mini red button js-agent-user-picker-remove';
      remove.textContent = removeText;
      trailing.append(remove);
      item.append(input, label, trailing);
      list.append(item);
    });

    list.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.js-agent-user-picker-remove');
      if (!target) return;
      target.closest('.item')!.remove();
    });
  }
}

// the recurring scan is switched on by the "scheduled scan" trigger event, its interval is
// pointless without it
function initAgentSecurityScheduleToggle(): void {
  const eventsInput = document.querySelector<HTMLInputElement>('input[name="action_events_security_scan"]');
  const settings = document.querySelector<HTMLElement>('.js-agent-security-schedule-settings');
  if (!eventsInput || !settings) return;

  const update = () => {
    const scheduled = eventsInput.value.split(',').some((event) => event.trim() === 'security_scan');
    settings.classList.toggle('tw-hidden', !scheduled);
  };
  eventsInput.addEventListener('change', update);
  update();
}

// the "match actions" logo has to follow the selected capability, so the create form always
// previews the avatar the new agent is going to get
export function initAgentAvatarPresets(): void {
  const container = document.querySelector<HTMLElement>('.js-agent-avatar-presets');
  const autoImage = container?.querySelector<HTMLImageElement>('.js-agent-avatar-auto-image');
  if (!container || !autoImage) return;

  type AgentAvatarRules = {rules: Array<[string, string]>; fallback: string};
  const rulesData = JSON.parse(container.getAttribute('data-avatar-rules') || '{"rules":[],"fallback":"general"}') as AgentAvatarRules;
  const images = new Map<string, string>();
  for (const input of container.querySelectorAll<HTMLInputElement>('input[data-preset]')) {
    const image = input.closest('label')?.querySelector<HTMLImageElement>('img');
    if (image) images.set(input.getAttribute('data-preset')!, image.getAttribute('src')!);
  }

  const capabilityInputs = document.querySelectorAll<HTMLInputElement>('.js-agent-capability');
  const update = () => {
    const capability = document.querySelector<HTMLInputElement>('.js-agent-capability:checked')?.value || '';
    const preset = rulesData.rules.find(([action]) => action === capability)?.[1] || rulesData.fallback;
    const image = images.get(preset);
    if (image) autoImage.setAttribute('src', image);
  };
  for (const input of capabilityInputs) {
    input.addEventListener('change', update);
  }
  update();
}

type AgentPermissionUserSearchResponse = {data: Array<{login: string; avatar_url: string; full_name: string; is_active: boolean}>};
type AgentPermissionTeamSearchResponse = {data: Array<{name: string; permission: string}>};

export function initAgentPermissionSearchBoxes(): void {
  const userBox = document.querySelector<HTMLElement>('#agent-permission-user-search');
  if (userBox) {
    const inactiveText = userBox.getAttribute('data-inactive-text')!;
    // the EE endpoint also returns inactive users, they can be listed before their account is activated
    attachSearchBox(userBox, `${appSubUrl}/user/search_agent_candidates?q={query}`, (response: AgentPermissionUserSearchResponse) => {
      const items: SearchResult[] = response.data.map((item) => ({
        title: item.login,
        image: item.avatar_url,
        description: item.is_active ? item.full_name : [item.full_name, inactiveText].filter(Boolean).join(' · '),
      }));
      return items;
    });
  }

  const teamBox = document.querySelector<HTMLElement>('#agent-permission-team-search');
  if (teamBox) {
    const orgName = teamBox.getAttribute('data-org-name');
    if (orgName) {
      attachSearchBox(teamBox, `${appSubUrl}/org/${orgName}/teams/-/search?q={query}`, (response: AgentPermissionTeamSearchResponse) => {
        return response.data.map((item) => ({title: item.name, description: item.permission}));
      });
    }
  }
}

export function initAgentPermissionCommandMode(): void {
  const modeSelect = document.querySelector<HTMLSelectElement>('select[name="command_mode"]');
  if (!modeSelect) return;
  const principals = document.querySelector<HTMLElement>('.js-agent-command-principals');
  if (!principals) return;

  const updateVisibility = () => {
    principals.classList.toggle('tw-hidden', modeSelect.value !== 'listed');
  };
  modeSelect.addEventListener('change', updateVisibility);
  updateVisibility();
}

function initAgentPermissionPrincipalList(options: {
  searchID: string;
  addSelector: string;
  listSelector: string;
  removeSelector: string;
  itemClass: string;
  inputName: string;
}) {
  const searchBox = document.querySelector<HTMLElement>(`#${options.searchID}`);
  if (!searchBox) return;
  const addButton = document.querySelector<HTMLButtonElement>(options.addSelector);
  const list = document.querySelector<HTMLElement>(options.listSelector);
  if (!addButton || !list) return;
  const removeText = list.getAttribute('data-remove-text') || 'Remove';

  const addItem = (value: string) => {
    value = value.trim();
    if (!value) return;
    const existing = list.querySelector<HTMLInputElement>(`input[name="${CSS.escape(options.inputName)}"][value="${CSS.escape(value)}"]`);
    if (existing) return;

    const item = document.createElement('div');
    item.className = `item tw-items-center ${options.itemClass}`;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = options.inputName;
    input.value = value;
    const label = document.createElement('div');
    label.className = 'item-main';
    label.textContent = value;
    const trailing = document.createElement('div');
    trailing.className = 'item-trailing';
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = `ui mini red button ${options.removeSelector.substring(1)}`;
    remove.textContent = removeText;
    trailing.append(remove);
    item.append(input, label, trailing);
    list.append(item);
  };

  addButton.addEventListener('click', () => {
    const prompt = searchBox.querySelector<HTMLInputElement>('input.prompt');
    if (!prompt) return;
    addItem(prompt.value);
    prompt.value = '';
  });

  list.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>(options.removeSelector);
    if (!target) return;
    target.closest('.item')?.remove();
  });
}

export function initAgentPermissionPrincipalLists(): void {
  initAgentPermissionPrincipalList({
    searchID: 'agent-permission-user-search',
    addSelector: '.js-agent-permission-add-user',
    listSelector: '.js-agent-permission-user-list',
    removeSelector: '.js-agent-permission-remove-user',
    itemClass: 'js-agent-permission-user-item',
    inputName: 'command_users',
  });
  initAgentPermissionPrincipalList({
    searchID: 'agent-permission-team-search',
    addSelector: '.js-agent-permission-add-team',
    listSelector: '.js-agent-permission-team-list',
    removeSelector: '.js-agent-permission-remove-team',
    itemClass: 'js-agent-permission-team-item',
    inputName: 'command_teams',
  });
}

export function initAgentCodetServerModelReload(): void {
  const sourceSelects = document.querySelectorAll<HTMLSelectElement>('.js-codet-server-source-select');
  if (!sourceSelects.length) return;

  for (const sourceSelect of sourceSelects) {
    const form = sourceSelect.closest('form');
    if (!form) continue;
    const modelSelect = form.querySelector<HTMLSelectElement>('.js-agent-model-select');
    if (!modelSelect) continue;
    const modelsURL = sourceSelect.getAttribute('data-models-url');
    if (!modelsURL) continue;
    const languageSelect = form.querySelector<HTMLSelectElement>('.js-agent-language-select');

    sourceSelect.addEventListener('change', async () => {
      const selectedModel = modelSelect.value || modelSelect.getAttribute('data-selected-model') || '';
      const selectedLanguage = languageSelect?.value || languageSelect?.getAttribute('data-selected-language') || '';
      const params = new URLSearchParams({codet_server_id: sourceSelect.value});
      const response = await fetch(`${modelsURL}?${params.toString()}`); // eslint-disable-line no-restricted-globals
      const data = await response.json();
      if (!response.ok || !data.ok) return;

      modelSelect.replaceChildren();
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = modelSelect.getAttribute('data-placeholder') || '';
      modelSelect.append(placeholder);

      for (const model of data.models ?? []) {
        const option = document.createElement('option');
        option.value = model.id;
        option.textContent = model.title;
        if (model.id === selectedModel) option.selected = true;
        modelSelect.append(option);
      }

      // the working languages are reported by the same codet server, refresh them together
      if (!languageSelect) return;
      const autoText = languageSelect.getAttribute('data-auto-text')!;
      languageSelect.replaceChildren();
      for (const language of data.languages ?? []) {
        const option = document.createElement('option');
        option.value = language.value;
        option.textContent = language.value === 'auto' ? autoText : language.title;
        if (language.value === selectedLanguage) option.selected = true;
        languageSelect.append(option);
      }
    });
  }
}
